import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ensureUnreachable } from '../../../../../common/src/util.ts';
import { ButtonColumn, buttonColumnGap, learnButtonHeight, PrimaryButton, SecondaryButton } from '../learn_buttons.tsx';
import { ScreenBody, Slack } from '../learn_screen_parts.tsx';
import { sameBubble, ShownBubble, stabilizeBubble } from './range_bubble_stabilizer.ts';
import {
  DoneState,
  isHinted,
  isNoisy,
  isScoring,
  isTroubleHearing,
  ListenState,
  RangeDirection,
  StepState,
} from './range_finder_session.ts';
import { HearingYouMeter } from './range_screen_parts.tsx';
import {
  advance,
  beginChoreography,
  eventFor,
  isFinished,
  keyframeAt,
  StairsChoreography,
  StairsKeyframe,
} from './range_stairs_choreography.ts';
import { sceneModelFor, StairsBubble, StairsSceneModel } from './range_stairs_model.ts';
import { RangeStairsScene, usePrefersReducedMotion } from './range_stairs_scene.tsx';

// One screen for every phase of a direction of the Range Finder (issue #3510): while the machine
// listens for the note the singer picks, while it is stepping them past it one scale note at a
// time, and while the direction is over. Presentational, so a story can render any moment from a
// state it builds by hand.
//
// The screen is the scene and the meter, and nothing else (issue #3510, addendum 3). The prompt,
// the nudge and hint lines and the trouble-hearing footer message are all gone: the singer on the
// stairs says all of it in his speech bubble, which is the one channel of instruction now, and
// what he says comes from the scene model. The scene is centred between the progress segments and
// the meter by a slack above and below it, and under the meter the foot always reserves the height
// of the stepping phase's two buttons, so the meter and the scene do not move when the buttons
// arrive with the target or leave with it. Only the stepping phase has buttons: while the machine
// listens the singer just sings, and it never gives up on them.
//
// The screen is also what drives the scene's choreography (issue #3510, addendum 3). Every render
// it compares the scene model with the last one and hands the choreography whatever event that
// implies - a note credited, the direction ended, a direction begun - and while anything is moving
// it draws frames on requestAnimationFrame, which is the only clock in the scene. The direction's
// ending is the one animation the state machine waits for: when the whole sequence (the landing
// hop, the pause, the bubble's fade, the handover and its announcement) has run, `onDone` tells the
// page to move on. Nothing here is saved, so leaving mid-sequence is just an unmount.
//
// The screen owns one more clock of its own (issue #3510, addendum 4): what the singer says is the
// model's bubble run through the stabilizer, so a line the machine changes its mind about inside
// bubbleMinShowMs never reaches the screen at all. While a bubble is waiting its turn the loop goes
// on drawing frames, which is what lands the switch on time rather than whenever the machine next
// has something to say.

export const rangeDirectionScreenId = 'range-direction';
export const rangeReplayButtonId = 'range-replay-button';
export const rangeStopButtonId = 'range-stop-button';

export function stopButtonText(direction: RangeDirection): string {
  switch (direction) {
    case 'lower':
      return "That's as low as I go";
    case 'higher':
      return "That's as high as I go";
    default:
      return ensureUnreachable(direction);
  }
}

// What each phase puts on the body for a story or a test to scope itself to; the phase and the
// direction are on every one of them
function phaseAttributes(state: ListenState | StepState | DoneState): Record<string, string | number> {
  if (state.phase === 'listen') {
    // Either kind of nothing heard for long enough: the bubble tells them apart (data-kind on it)
    return { 'data-trouble': isTroubleHearing(state) || isNoisy(state) ? 'true' : 'false' };
  }
  if (state.phase === 'step') {
    return {
      'data-target': state.targetMidi,
      'data-scoring': isScoring(state) ? 'true' : 'false',
      'data-hinted': isHinted(state) ? 'true' : 'false',
      'data-nudged': state.nudged ? 'true' : 'false',
    };
  }
  return {};
}

export const RangeDirectionScreen = ({
  state,
  onReplay,
  onStop,
  onDone,
  sceneSpeed = 1,
}: {
  state: ListenState | StepState | DoneState;
  onReplay: () => void;
  onStop: () => void;
  // The direction's ending has run its course; the page moves the machine on
  onDone?: () => void;
  // How many times faster than life the scene runs; only a story ever asks for anything but 1
  sceneSpeed?: number;
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const model = sceneModelFor(state);
  const { keyframe, bubble } = useStairsScene(model, reducedMotion, sceneSpeed, onDone);
  return (
    <ScreenBody
      id={rangeDirectionScreenId}
      data-phase={state.phase}
      data-direction={state.direction}
      {...phaseAttributes(state)}
    >
      <Slack />
      <RangeStairsScene model={model} bubble={bubble} keyframe={keyframe} reducedMotion={reducedMotion} />
      <Slack />
      <HearingYouMeter level={state.level} />
      <Foot>
        {state.phase === 'step' && (
          <ButtonColumn>
            <SecondaryButton id={rangeReplayButtonId} data-testid={rangeReplayButtonId} onClick={onReplay}>
              Play note again
            </SecondaryButton>
            <PrimaryButton id={rangeStopButtonId} data-testid={rangeStopButtonId} onClick={onStop}>
              {stopButtonText(state.direction)}
            </PrimaryButton>
          </ButtonColumn>
        )}
      </Foot>
    </ScreenBody>
  );
};

// The scene's frame and the singer's line, now. The choreography, the model it was last given and
// the bubble on screen are refs rather than state: they change on a clock of their own, and only
// what the scene draws has to reach React. The loop runs only while something is moving - or while
// a bubble is waiting out the one before it - so a screen that is standing still, which is most of
// a run, re-renders no more often than the state machine makes it.
function useStairsScene(
  model: StairsSceneModel,
  reducedMotion: boolean,
  sceneSpeed: number,
  onDone?: () => void,
): { keyframe: StairsKeyframe; bubble: StairsBubble } {
  // Running the scene faster is running its clock faster: every duration in the choreography, and
  // the bubble's minimum with them, is a length of this clock, so one multiplication does the lot
  const speedRef = useRef(sceneSpeed);
  const [start] = useState(() => {
    const atMs = performance.now() * sceneSpeed;
    const choreography = beginChoreography(model, atMs, reducedMotion);
    return {
      choreography,
      keyframe: keyframeAt(choreography, atMs),
      shown: { bubble: model.bubble, sinceMs: atMs },
    };
  });
  const choreographyRef = useRef<StairsChoreography>(start.choreography);
  const previousRef = useRef<StairsSceneModel>(model);
  const reportedRef = useRef(false);
  const onDoneRef = useRef(onDone);
  const [keyframe, setKeyframe] = useState<StairsKeyframe>(start.keyframe);
  // What the singer is saying, what the state would have him say, and whether the two have yet to
  // meet. All three are refs as well as state because the loop reads them between renders.
  const shownRef = useRef<ShownBubble>(start.shown);
  const wantedRef = useRef<StairsBubble>(model.bubble);
  const pendingRef = useRef(false);
  const [shown, setShown] = useState<ShownBubble>(start.shown);
  // One run of the loop per event, counted so the effect below can own its own frame: its cleanup
  // is what cancels the run, which is how StrictMode's simulated remount gets a loop again rather
  // than a cancelled first frame and a flag saying one was already asked for (the arrow's draw-on
  // is that first frame, so in dev the arrow was never drawn at all).
  const [run, setRun] = useState(0);

  useEffect(() => {
    onDoneRef.current = onDone;
    speedRef.current = sceneSpeed;
  });

  // The bubble as of `atMs`, committed to React when it changes; true while the wanted bubble is
  // still waiting for the minimum, which is a reason to draw another frame. Refs only, so the loop
  // may hold on to one of these from the render it started on.
  const settleBubble = (atMs: number): boolean => {
    const next = stabilizeBubble(shownRef.current, wantedRef.current, atMs);
    if (next !== shownRef.current) {
      shownRef.current = next;
      setShown(next);
    }
    pendingRef.current = !sameBubble(next.bubble, wantedRef.current);
    return pendingRef.current;
  };

  // Whatever the state machine has just done to the scene, and the frames that follow from it. The
  // model is a fresh object every render, so this runs constantly and does nothing nearly every
  // time: `eventFor` and the stabilizer are what decide whether anything actually happened.
  //
  // A layout effect, and the first keyframe is set here rather than waited for from the loop:
  // React has already rendered the new model (the note on its tread, the next target lit, the
  // question in the bubble) and the browser would paint that against the last still frame before
  // the first animation frame ran - a flash of the scene as it will look when the hop is over. Set
  // before the paint, the hop's own first frame is committed with the model that caused it.
  useLayoutEffect(() => {
    // Whatever the browser is asking for now: a singer who turns less motion on mid-run gets it
    // from the next thing that moves
    const atMs = performance.now() * speedRef.current;
    wantedRef.current = model.bubble;
    const waiting = pendingRef.current;
    const pending = settleBubble(atMs);
    const event = eventFor(previousRef.current, model);
    previousRef.current = model;
    if (event) {
      choreographyRef.current = { ...advance(choreographyRef.current, event, atMs), reducedMotion };
      reportedRef.current = false;
      // This has to reach React before the browser paints too, or the new model is painted against
      // the last still keyframe for a frame (see above)
      setKeyframe(keyframeAt(choreographyRef.current, atMs));
    }
    // A run of the loop for the event, and one for a bubble that has just started waiting - the
    // start of the wait only: a loop already running will land the switch itself, and asking for
    // another every render would be a render for every render
    const startsWaiting = pending && !waiting;
    if (!event && !startsWaiting) {
      return;
    }
    setRun((previous) => previous + 1);
  }, [model, reducedMotion]);

  // The loop: frames for as long as the choreography says something is moving, or the bubble on
  // screen owes its place to the one waiting. The first one is a direction's arrow drawing itself on.
  useEffect(() => {
    let frame = 0;
    const draw = () => {
      const atMs = performance.now() * speedRef.current;
      const pending = settleBubble(atMs);
      const next = keyframeAt(choreographyRef.current, atMs);
      setKeyframe(next);
      if (isFinished(next.stage) && !reportedRef.current) {
        reportedRef.current = true;
        onDoneRef.current?.();
      }
      frame = next.animating || pending ? requestAnimationFrame(draw) : 0;
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [run]);

  return { keyframe, bubble: shown.bubble };
}

// Always as tall as the two buttons and the air around them, whether or not they are there: the
// column's own gap between them, and the same again between the meter and the first of them.
export const footHeight = learnButtonHeight * 2 + buttonColumnGap * 2;

const Foot = styled.div`
  flex: none;
  box-sizing: border-box;
  min-height: ${footHeight}px;
  padding-top: ${buttonColumnGap}px;
`;
